import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import CalendarNav from "./CalendarNav";
import { statusClass, eventClass, eventTypeLabel } from "@/lib/utils2";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Filter, Move, AlertTriangle, HeartPulse, ShieldAlert } from "lucide-react";
import { useAuth } from "@/lib/auth";

const DAY_PX = 30; // width of each date column
const ROW_PX = 36; // height of each employee row

function iso(d) {
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}
function startOfWeekMon(d) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

/**
 * Gantt-style Team Calendar.
 * - One row per employee
 * - Dates across the top
 * - Holiday bars span the request's date range, coloured by status
 * - Click a bar → opens RequestDetailDialog
 * - Click an empty day cell → onAddRequest(date, employee) for admin add flow
 */
export default function TeamCalendar({
  reference,
  bankHolidays = [],
  onOpenRequest,
  onAddRequest,
  onChanged,
}) {
  const { user } = useAuth();
  const today = new Date();
  const [cursor, setCursor] = useState(startOfMonth(today));
  const [view, setView] = useState("month");
  const [filters, setFilters] = useState({
    department: "", site: "", supervisor_id: "",
    primary_role: "", secondary_role: "",
    employee_q: "", status: "approved,pending",
  });
  const [data, setData] = useState({ employees: [], total: 0 });
  const [loading, setLoading] = useState(false);

  // Drag state — admin only.
  const dragRef = useRef(null);
  const [dragPreview, setDragPreview] = useState(null); // {rid, leftShift, dayShift}
  const [confirmMove, setConfirmMove] = useState(null); // {request, newStart, newEnd, days, notify}

  // Compute the date window for the current view.
  const { startDate, endDate, dates } = useMemo(() => {
    let s, e;
    if (view === "week") {
      s = startOfWeekMon(cursor);
      e = new Date(s); e.setDate(s.getDate() + 6);
    } else {
      s = startOfMonth(cursor);
      e = endOfMonth(cursor);
    }
    const ds = [];
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) ds.push(new Date(d));
    return { startDate: s, endDate: e, dates: ds };
  }, [cursor, view]);

  const startIso = iso(startDate);
  const endIso = iso(endDate);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { start: startIso, end: endIso };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/admin/team-calendar", { params })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ employees: [], total: 0 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [startIso, endIso, JSON.stringify(filters)]); // eslint-disable-line

  const bhMap = useMemo(() => {
    const m = new Map();
    for (const b of bankHolidays) m.set(b.date, b.name);
    return m;
  }, [bankHolidays]);

  const todayIso = iso(today);

  // Helper to compute bar's x-position and width within a row.
  const barGeom = (r) => {
    const rStart = new Date(r.start_date);
    const rEnd = new Date(r.end_date);
    const visStart = rStart < startDate ? startDate : rStart;
    const visEnd = rEnd > endDate ? endDate : rEnd;
    const offset = Math.round((visStart - startDate) / 86400000);
    const width = Math.round((visEnd - visStart) / 86400000) + 1;
    return { left: offset * DAY_PX, width: width * DAY_PX };
  };

  // Admin drag-and-drop on bars.
  const isAdmin = !!user?.is_admin;
  const onBarPointerDown = (ev, r) => {
    if (!isAdmin) return;
    if (r.status === "rejected" || r.status === "cancelled") return; // don't allow moving finalised cancellations
    ev.preventDefault();
    ev.stopPropagation();
    const startX = ev.clientX;
    dragRef.current = { request: r, startX, dayShift: 0 };
    setDragPreview({ rid: r.id, dayShift: 0 });
    const onMove = (e) => {
      if (!dragRef.current) return;
      const shiftPx = e.clientX - dragRef.current.startX;
      const dayShift = Math.round(shiftPx / DAY_PX);
      dragRef.current.dayShift = dayShift;
      setDragPreview({ rid: r.id, dayShift });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      const drag = dragRef.current;
      dragRef.current = null;
      setDragPreview(null);
      if (!drag || drag.dayShift === 0) return;
      const shiftMs = drag.dayShift * 86400000;
      const newStart = iso(new Date(new Date(r.start_date).getTime() + shiftMs));
      const newEnd = iso(new Date(new Date(r.end_date).getTime() + shiftMs));
      const days = Math.round((new Date(newEnd) - new Date(newStart)) / 86400000) + 1;
      setConfirmMove({ request: r, newStart, newEnd, days, notify: false, clashes: null, clashLoading: true });
      // Re-run clash detection against the new dates.
      api.post("/holiday-requests/check-clashes", {
        employee_id: r.employee_id || null,
        first_name: r.first_name,
        last_name: r.last_name,
        email: r.email,
        department: r.department,
        job_role: r.job_role,
        start_date: newStart,
        end_date: newEnd,
        exclude_id: r.id,
      }).then(({ data }) => {
        setConfirmMove((prev) => prev && prev.request.id === r.id
          ? { ...prev, clashes: data, clashLoading: false }
          : prev);
      }).catch(() => {
        setConfirmMove((prev) => prev && prev.request.id === r.id
          ? { ...prev, clashes: { hard_block: [], warnings: [] }, clashLoading: false }
          : prev);
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const cancelMove = () => setConfirmMove(null);
  const commitMove = async () => {
    const m = confirmMove;
    if (!m) return;
    try {
      await api.patch(`/holiday-requests/${m.request.id}`, {
        start_date: m.newStart,
        end_date: m.newEnd,
        days: m.days,
        reason: `Drag-and-drop reschedule from ${m.request.start_date} → ${m.request.end_date}`,
        notify_employee: !!m.notify,
        notify_supervisor: !!m.notify,
      });
      toast.success(`Moved to ${m.newStart} → ${m.newEnd}${m.notify ? " · emails sent" : ""}`);
      setConfirmMove(null);
      onChanged?.();
      // Reload data
      const params = { start: startIso, end: endIso };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/admin/team-calendar", { params });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to move");
    }
  };

  const clearFilters = () => setFilters({
    department: "", site: "", supervisor_id: "",
    primary_role: "", secondary_role: "",
    employee_q: "", status: "approved,pending",
  });

  return (
    <div className="space-y-4" data-testid="team-calendar">
      <CalendarNav
        cursor={cursor}
        onCursor={setCursor}
        view={view}
        onView={setView}
        testIdPrefix="team-cal"
        label={
          view === "week"
            ? `Week of ${dates[0]?.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`
            : cursor.toLocaleString(undefined, { month: "long", year: "numeric" })
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center text-sm">
        <Filter className="w-4 h-4 text-muted-foreground"/>
        <Input data-testid="team-filter-q" placeholder="Contractor / email…" className="max-w-[200px]"
          value={filters.employee_q} onChange={(e) => setFilters({ ...filters, employee_q: e.target.value })}/>
        <FilterSelect testid="team-filter-status" placeholder="All statuses" value={filters.status}
          onChange={(v) => setFilters({ ...filters, status: v })}
          options={[
            {value:"approved,pending",label:"Approved & Pending"},
            {value:"approved",label:"Approved only"},
            {value:"pending",label:"Pending only"},
            {value:"approved,pending,rejected,cancelled",label:"All statuses"},
          ]}/>
        <FilterSelect testid="team-filter-dept" placeholder="All departments" value={filters.department}
          onChange={(v) => setFilters({ ...filters, department: v })}
          options={(reference?.departments || []).map((d) => ({ value: d.name, label: d.name }))}/>
        <FilterSelect testid="team-filter-site" placeholder="All sites" value={filters.site}
          onChange={(v) => setFilters({ ...filters, site: v })}
          options={(reference?.sites || []).map((s) => ({ value: s.name, label: s.name }))}/>
        <FilterSelect testid="team-filter-supervisor" placeholder="All supervisors" value={filters.supervisor_id}
          onChange={(v) => setFilters({ ...filters, supervisor_id: v })}
          options={(reference?.supervisors || []).map((s) => ({ value: s.id, label: s.name }))}/>
        <FilterSelect testid="team-filter-primary" placeholder="All primary roles" value={filters.primary_role}
          onChange={(v) => setFilters({ ...filters, primary_role: v })}
          options={(reference?.job_roles || []).map((r) => ({ value: r, label: r }))}/>
        <FilterSelect testid="team-filter-secondary" placeholder="All secondary roles" value={filters.secondary_role}
          onChange={(v) => setFilters({ ...filters, secondary_role: v })}
          options={(reference?.job_roles || []).map((r) => ({ value: r, label: r }))}/>
        <Button data-testid="team-clear-filters" variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>
        <div className="ml-auto text-xs text-muted-foreground" data-testid="team-result-count">
          {loading ? "Loading…" : `${data.total} employees · ${dates.length} days`}
        </div>
      </div>

      {/* Gantt grid */}
      <div className="border border-border rounded-lg overflow-hidden bg-[#1E1E1E]">
        <div className="overflow-x-auto" data-testid="team-gantt-scroll">
          <div style={{ minWidth: 260 + dates.length * DAY_PX }}>
            {/* Header row */}
            <div className="flex sticky top-0 z-20 bg-[#1E1E1E] border-b border-border">
              <div className="w-[260px] shrink-0 px-3 py-2 text-[11px] uppercase tracking-[0.08em] font-semibold text-muted-foreground border-r border-border bg-[#1E1E1E]">
                Contractor
              </div>
              <div className="flex" style={{ width: dates.length * DAY_PX }}>
                {dates.map((d) => {
                  const k = iso(d);
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  const isBh = bhMap.has(k);
                  const isToday = k === todayIso;
                  return (
                    <div
                      key={k}
                      data-testid={`team-cal-header-${k}`}
                      title={isBh ? `Bank holiday — ${bhMap.get(k)}` : d.toLocaleDateString()}
                      className={[
                        "shrink-0 text-center py-1 border-r border-border/60",
                        isWeekend ? "bg-[#141414]" : "bg-[#1E1E1E]",
                        isBh ? "bg-purple-100" : "",
                        isToday ? "ring-1 ring-primary ring-inset" : "",
                      ].join(" ")}
                      style={{ width: DAY_PX }}
                    >
                      <div className="text-[10px] uppercase text-muted-foreground">{d.toLocaleDateString(undefined, { weekday: "narrow" })}</div>
                      <div className={`text-xs tabular-nums ${isToday ? "font-bold text-primary" : ""}`}>{d.getDate()}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Body */}
            <div data-testid="team-gantt-body">
              {data.employees.length === 0 && (
                <div className="px-4 py-10 text-center text-sm text-muted-foreground">No employees match these filters.</div>
              )}
              {data.employees.map((e) => (
                <div key={e.id} data-testid={`team-row-${e.id}`} className="flex items-stretch border-b border-border/60 hover:bg-muted/20" style={{ minHeight: ROW_PX }}>
                  <div className="w-[260px] shrink-0 px-3 py-1.5 border-r border-border bg-[#1E1E1E] sticky left-0">
                    <div className="text-sm font-medium truncate">{e.first_name} {e.last_name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {e.department}{e.site ? ` · ${e.site}` : ""}
                      {(e.job_roles && e.job_roles[0]) ? ` · ${e.job_roles[0]}` : ""}
                    </div>
                  </div>
                  <div className="relative" style={{ width: dates.length * DAY_PX, height: ROW_PX }}>
                    {/* Day cells (clickable for add) + weekend / bank holiday tint */}
                    <div className="absolute inset-0 flex">
                      {dates.map((d) => {
                        const k = iso(d);
                        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                        const isBh = bhMap.has(k);
                        const isToday = k === todayIso;
                        return (
                          <button
                            key={k}
                            data-testid={`team-cell-${e.id}-${k}`}
                            onClick={() => onAddRequest?.(d, e)}
                            title={isBh ? `Bank holiday — ${bhMap.get(k)}` : `Add absence for ${e.first_name} ${e.last_name} on ${k}`}
                            className={[
                              "shrink-0 border-r border-border/40 transition-colors",
                              isWeekend ? "bg-[#141414]" : "bg-[#1E1E1E]",
                              isBh ? "bg-purple-100" : "",
                              isToday ? "ring-1 ring-primary/40 ring-inset" : "",
                              "hover:bg-accent/40 cursor-pointer",
                            ].join(" ")}
                            style={{ width: DAY_PX }}
                          />
                        );
                      })}
                    </div>
                    {/* Holiday bars (absolutely positioned over the grid) */}
                    {e.requests.map((r) => {
                      const { left, width } = barGeom(r);
                      const preview = dragPreview && dragPreview.rid === r.id ? dragPreview.dayShift * DAY_PX : 0;
                      const isSickness = (r.request_type || "").toLowerCase() === "sickness";
                      const narrow = width < 60;
                      return (
                        <button
                          key={r.id}
                          data-testid={`team-bar-${r.id}`}
                          data-request-type={r.request_type}
                          onPointerDown={(ev) => onBarPointerDown(ev, r)}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            // Suppress click if user dragged (handled by pointerup), trust dayShift==0 in dragRef
                            if (dragRef.current && dragRef.current.dayShift !== 0) return;
                            onOpenRequest?.(r);
                          }}
                          title={`${r.first_name} ${r.last_name} · ${r.start_date} → ${r.end_date} · ${isSickness ? "Sickness" : r.status} · ${r.days}d${isAdmin ? " · drag to reschedule" : ""}`}
                          className={`absolute top-1.5 h-[24px] rounded-md text-[10px] text-left ${narrow ? "px-1 flex items-center justify-center" : "px-2 truncate"} hover:opacity-90 ${eventClass(r)} ${isAdmin ? "cursor-grab active:cursor-grabbing" : ""} ${preview ? "ring-2 ring-primary opacity-80" : ""}`}
                          style={{ left: left + preview, width, touchAction: "none" }}
                        >
                          {narrow ? (
                            isSickness ? <HeartPulse className="w-3 h-3"/> : <span className="font-bold text-[11px] leading-none">·</span>
                          ) : (
                            <>
                              {isAdmin && <Move className="inline w-2.5 h-2.5 mr-0.5 opacity-70"/>}
                              <span className="font-medium">{eventTypeLabel(r)}</span>
                              <span className="opacity-80"> · {r.days}d</span>
                            </>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {["pending","approved","rejected","cancelled"].map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded-sm ${statusClass(s)}`}/>
            {s}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm status-sickness"/>
          sickness
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-purple-100 border border-purple-300"/>
          bank holiday
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-muted/60 border border-border"/>
          weekend
        </span>
        {isAdmin && (
          <span className="inline-flex items-center gap-1.5 ml-2 text-primary">
            <Move className="w-3 h-3"/> drag bars to reschedule
          </span>
        )}
      </div>

      {/* Drag-and-drop confirmation (admin only) */}
      <Dialog open={!!confirmMove} onOpenChange={(o) => { if (!o) cancelMove(); }}>
        <DialogContent className="max-w-md w-[95vw]" data-testid="drag-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm reschedule</DialogTitle>
          </DialogHeader>
          {confirmMove && (
            <div className="text-sm space-y-3">
              <div>
                Move <strong>{confirmMove.request.first_name} {confirmMove.request.last_name}</strong>&apos;s absence:
              </div>
              <div className="rounded-md border border-border p-2.5 text-xs space-y-1">
                <div><span className="text-muted-foreground w-12 inline-block">From:</span> {confirmMove.request.start_date} → {confirmMove.request.end_date} ({confirmMove.request.days}d)</div>
                <div><span className="text-muted-foreground w-12 inline-block">To:</span> <strong className="text-foreground">{confirmMove.newStart} → {confirmMove.newEnd}</strong> ({confirmMove.days}d)</div>
              </div>

              {/* Clash detection results */}
              {confirmMove.clashLoading && (
                <div className="text-xs text-muted-foreground" data-testid="drag-clash-loading">Checking conflicts…</div>
              )}
              {confirmMove.clashes && confirmMove.clashes.hard_block.length > 0 && (
                <div className="rounded-md border border-[#5E1F1F] bg-[#1F0A0A] p-2.5 text-[#F87171] text-xs" data-testid="drag-clash-hard">
                  <div className="flex items-start gap-2">
                    <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0"/>
                    <div className="space-y-1">
                      <div className="font-semibold">This contractor already has overlapping absences on the new dates</div>
                      <ul className="space-y-0.5">
                        {confirmMove.clashes.hard_block.slice(0, 3).map((c) => (
                          <li key={c.id}>· {c.start_date} → {c.end_date} ({c.status})</li>
                        ))}
                      </ul>
                      <div className="opacity-80">You can still save (admin override) but please double-check.</div>
                    </div>
                  </div>
                </div>
              )}
              {confirmMove.clashes && confirmMove.clashes.warnings.length > 0 && (
                <div className="rounded-md border border-[#8A6E1F] bg-[#1F1808] p-2.5 text-[#FCD34D] text-xs" data-testid="drag-clash-warn">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
                    <div className="space-y-1">
                      <div className="font-semibold">
                        {confirmMove.clashes.warnings.length} other {confirmMove.request.job_role || "team member"}{confirmMove.clashes.warnings.length === 1 ? "" : "s"} in {confirmMove.request.department} also off these dates
                      </div>
                      <ul className="space-y-0.5">
                        {confirmMove.clashes.warnings.slice(0, 4).map((c) => (
                          <li key={c.id}>· {c.first_name} {c.last_name} · {c.start_date} → {c.end_date}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              {confirmMove.clashes && confirmMove.clashes.hard_block.length === 0 && confirmMove.clashes.warnings.length === 0 && (
                <div className="text-xs text-emerald-700" data-testid="drag-clash-clear">No conflicts on the new dates.</div>
              )}

              <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                <Checkbox
                  data-testid="drag-notify-checkbox"
                  checked={confirmMove.notify}
                  onCheckedChange={(v) => setConfirmMove({ ...confirmMove, notify: !!v })}
                />
                Email the contractor <em>and</em> their supervisor about the new dates
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={cancelMove} data-testid="drag-cancel-btn">Cancel</Button>
            <Button onClick={commitMove} data-testid="drag-confirm-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterSelect({ testid, placeholder, value, onChange, options }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="h-9 max-w-[180px]"><SelectValue placeholder={placeholder}/></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all">{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
